import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  Video,
  Image,
  Heart,
  Grid,
  List,
  ChevronDown,
  MessageCircle,
  Bookmark,
  Search,
  X,
} from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import ApiService from '../services/ApiService';

function RelativeProgressView({ relative, currentUserUuid, onBack, onShowToast, contentCache, onContentLoaded, homeFeedContent, contentManifest, onRelativeUpdated }) {
  const [contents, setContents] = useState([]);
  const [editingRelationship, setEditingRelationship] = useState(false);
  const [relationshipValue, setRelationshipValue] = useState(relative.relationship || '');
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [currentVideo, setCurrentVideo] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchActive, setSearchActive] = useState(false);

  // Keep refs for saving cache on unmount
  const contentsRef = useRef(contents);
  const favoritesRef = useRef(favorites);
  contentsRef.current = contents;
  favoritesRef.current = favorites;

  useEffect(() => {
    loadContent();
    // Save cache on unmount with latest state
    return () => {
      const allContent = [...contentsRef.current, ...favoritesRef.current];
      if (allContent.length > 0) {
        onContentLoaded?.({ contentArray: allContent });
      }
    };
  }, [relative]);

  const initFromArray = (contentArray) => {
    const favs = contentArray.filter(c => c.favorite);
    const regular = contentArray.filter(c => !c.favorite);
    setFavorites(favs);
    setContents(regular);
    const expanded = { Bookmarked: true };
    regular.forEach(c => {
      expanded[normalizeCategory(c.question?.questionCategory)] = true;
    });
    setExpandedCategories(expanded);
  };

  const dedup = (arr) => {
    const seen = new Set();
    return arr.filter(c => {
      if (seen.has(c.uuid)) return false;
      seen.add(c.uuid);
      return true;
    });
  };

  const loadContent = async () => {
    // Use cache if available and less than 5 minutes old
    if (contentCache && (Date.now() - contentCache.timestamp) < 5 * 60 * 1000) {
      initFromArray(dedup(contentCache.contentArray));
      setLoading(false);
      return;
    }

    // Use already-resolved home feed content if available
    if (homeFeedContent && homeFeedContent.length > 0) {
      const filtered = dedup(homeFeedContent.filter(c => c.userUuid === relative.uuid));
      if (filtered.length > 0) {
        initFromArray(filtered);
        onContentLoaded?.({ contentArray: filtered });
        setLoading(false);
        return;
      }
    }

    // Use manifest + batch resolve if manifest is available
    if (contentManifest && contentManifest.length > 0) {
      const relativeEntries = contentManifest.filter(e => e.userUuid === relative.uuid);
      if (relativeEntries.length > 0) {
        setLoading(true);
        try {
          const uuids = relativeEntries.map(e => e.uuid);
          // Batch in chunks of 50
          const allPreviews = [];
          for (let i = 0; i < uuids.length; i += 50) {
            const batch = uuids.slice(i, i + 50);
            const data = await ApiService.getContentBatch(batch);
            allPreviews.push(...(data.contentPreviewArray || []));
          }
          // Filter to only content recorded by this relative
          const contentArray = dedup(allPreviews)
            .filter(c => c.userUuid === relative.uuid);
          initFromArray(contentArray);
          onContentLoaded?.({ contentArray });
        } catch (error) {
          onShowToast?.('Failed to load content', 'error');
        }
        setLoading(false);
        return;
      }
    }

    // Fallback: use the old single-call endpoint
    setLoading(true);
    try {
      const data = await ApiService.getContentForRelative(relative.uuid);
      // The API always includes the requesting user's own content, so filter
      // to only content recorded by this specific relative.
      const contentArray = dedup(data.contentPreviewArray || [])
        .filter(c => c.userUuid === relative.uuid);
      initFromArray(contentArray);
      onContentLoaded?.({ contentArray });
    } catch (error) {
      onShowToast?.('Failed to load content', 'error');
    }
    setLoading(false);
  };

  const isUrlExpired = (expiryDate) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const buffer = 10 * 60 * 1000; // 10 minutes
    return Date.now() > expiry.getTime() - buffer;
  };

  const refreshUrlsIfNeeded = async (content) => {
    if (isUrlExpired(content.urlsExpiryDate)) {
      try {
        const refreshed = await ApiService.refreshContentUrls(content.uuid);
        const updateContent = (c) =>
          c.uuid === content.uuid
            ? { ...c, primaryUrl: refreshed.primaryUrl, secondaryUrl: refreshed.secondaryUrl, previewUrl: refreshed.previewUrl, urlsExpiryDate: refreshed.urlsExpiryDate }
            : c;
        setContents(prev => prev.map(updateContent));
        setFavorites(prev => prev.map(updateContent));
        return { ...content, ...refreshed };
      } catch (error) {
        onShowToast?.('Failed to refresh content URLs', 'error');
      }
    }
    return content;
  };

  const filterBySearch = (contentArray) => {
    if (!searchQuery.trim()) return contentArray;
    const q = searchQuery.toLowerCase();
    return contentArray.filter(c => {
      const summary = (c.question?.summary || '').toLowerCase();
      const question = (c.question?.question || '').toLowerCase();
      const category = (c.question?.questionCategory || '').toLowerCase();
      const date = new Date(c.creation).toLocaleDateString().toLowerCase();
      return summary.includes(q) || question.includes(q) || category.includes(q) || date.includes(q);
    });
  };

  const CATEGORY_ORDER = [
    'ChildhoodMemories', 'TeenageYears', 'FamilyTraditions', 'LifeLessons',
    'CareerAndWork', 'HistoricalEvents', 'CulturalAndSocialPerspectives',
    'FamilyHistory', 'HealthAndWellness', 'TravelAndAdventures',
    'LoveAndRelationships', 'HobbiesAndPassions', 'CreativeAndArtisticExpression',
    'Personality', 'ExperiencesAndMilestones', 'SpiritualityAndReligion',
    'FutureGoalsAndAspirations', 'Moment', 'WeeklyCallout',
  ];

  const normalizeCategory = (cat) => {
    if (!cat || cat === 'None' || cat === 'Other' || cat === 'QuickMoment' || cat === 'QuickMoments' || cat === 'Moments') return 'Moment';
    if (cat === 'Weekly Callout') return 'WeeklyCallout';
    return cat;
  };

  const getSortedCategories = (categorized) => {
    return Object.keys(categorized).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      const aOrder = ai >= 0 ? ai : CATEGORY_ORDER.length;
      const bOrder = bi >= 0 ? bi : CATEGORY_ORDER.length;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.localeCompare(b);
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const openContent = async (content) => {
    const allContents = [...favorites, ...contents];
    const index = allContents.findIndex(c => c.uuid === content.uuid);
    const refreshed = await refreshUrlsIfNeeded(content);
    setCurrentVideoIndex(index);
    setCurrentVideo(refreshed);
  };

  const playNext = () => {
    const allContents = [...favorites, ...contents];
    const nextIndex = (currentVideoIndex + 1) % allContents.length;
    setCurrentVideoIndex(nextIndex);
    setCurrentVideo(allContents[nextIndex]);
  };

  const playPrevious = () => {
    const allContents = [...favorites, ...contents];
    const prevIndex = currentVideoIndex === 0 ? allContents.length - 1 : currentVideoIndex - 1;
    setCurrentVideoIndex(prevIndex);
    setCurrentVideo(allContents[prevIndex]);
  };

  const toggleFavorite = async (contentUuid) => {
    const isFav = favorites.some(f => f.uuid === contentUuid);
    try {
      await ApiService.toggleFavorite(contentUuid, isFav ? 'remove' : 'add');
      if (isFav) {
        const content = favorites.find(f => f.uuid === contentUuid);
        setFavorites(prev => prev.filter(f => f.uuid !== contentUuid));
        if (content) setContents(prev => [...prev, { ...content, favorite: false }]);
      } else {
        const content = contents.find(c => c.uuid === contentUuid);
        if (content) {
          setContents(prev => prev.filter(c => c.uuid !== contentUuid));
          setFavorites(prev => [...prev, { ...content, favorite: true }]);
        }
      }
    } catch (error) {
      onShowToast?.('Failed to update bookmark', 'error');
    }
  };

  const handleToggleLike = async (contentUuid) => {
    const allContents = [...favorites, ...contents];
    const content = allContents.find(c => c.uuid === contentUuid);
    if (!content) return;

    const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);
    try {
      const result = isLiked
        ? await ApiService.removeLike(contentUuid)
        : await ApiService.toggleLike(contentUuid);

      const updateContent = (c) =>
        c.uuid === contentUuid
          ? { ...c, likeCount: result.likeCount, likes: result.likes }
          : c;
      setContents(prev => prev.map(updateContent));
      setFavorites(prev => prev.map(updateContent));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({ ...prev, likeCount: result.likeCount, likes: result.likes }));
      }
    } catch (error) {
      onShowToast?.('Failed to update like', 'error');
    }
  };

  const handleAddComment = async (contentUuid, commentText) => {
    try {
      const result = await ApiService.addComment(contentUuid, commentText);
      const updateContent = (c) =>
        c.uuid === contentUuid
          ? { ...c, commentCount: result.commentCount, comments: [...(c.comments || []), result.comment] }
          : c;
      setContents(prev => prev.map(updateContent));
      setFavorites(prev => prev.map(updateContent));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: [...(prev.comments || []), result.comment]
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to add comment', 'error');
    }
  };

  const handleDeleteComment = async (contentUuid, commentUuid) => {
    try {
      const result = await ApiService.deleteComment(contentUuid, commentUuid);
      const updateContent = (c) =>
        c.uuid === contentUuid
          ? { ...c, commentCount: result.commentCount, comments: (c.comments || []).filter(cm => cm.uuid !== commentUuid) }
          : c;
      setContents(prev => prev.map(updateContent));
      setFavorites(prev => prev.map(updateContent));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(prev => ({
          ...prev,
          commentCount: result.commentCount,
          comments: (prev.comments || []).filter(cm => cm.uuid !== commentUuid)
        }));
      }
    } catch (error) {
      onShowToast?.('Failed to delete comment', 'error');
    }
  };

  const handleDeleteContent = async (contentUuid) => {
    try {
      await ApiService.deleteContent(contentUuid);
      setContents(prev => prev.filter(c => c.uuid !== contentUuid));
      setFavorites(prev => prev.filter(c => c.uuid !== contentUuid));
      if (currentVideo?.uuid === contentUuid) {
        setCurrentVideo(null);
      }
      onShowToast?.('Content deleted', 'success');
    } catch (error) {
      onShowToast?.('Failed to delete content', 'error');
    }
  };

  const getPreviewUrl = (content) => content.previewUrl || null;

  const renderContentGrid = (contentArray) => {
    if (viewMode === 'list') {
      return (
        <div className="space-y-2">
          {contentArray.map((content) => {
            const isImage = content.type === 'image';
            return (
              <div
                key={content.uuid}
                onClick={() => openContent(content)}
                className="bg-gray-800 p-4 rounded-lg flex items-center space-x-4 cursor-pointer hover:bg-gray-700 transition-colors"
              >
                <div className="w-20 h-20 bg-gray-600 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {getPreviewUrl(content) ? (
                    <img src={getPreviewUrl(content)} alt="" className="w-full h-full object-cover" />
                  ) : isImage ? (
                    <Image className="w-8 h-8 text-gray-400" />
                  ) : (
                    <Video className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{content.question?.summary || content.question?.question || (isImage ? 'Image' : 'Video')}</p>
                  <p className="text-sm text-gray-400">
                    {new Date(content.creation).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    {content.likeCount > 0 && (
                      <span className="flex items-center gap-1">
                        <Heart size={12} /> {content.likeCount}
                      </span>
                    )}
                    {content.commentCount > 0 && (
                      <span className="flex items-center gap-1">
                        <MessageCircle size={12} /> {content.commentCount}
                      </span>
                    )}
                  </div>
                </div>
                {content.favorite && (
                  <Bookmark className="w-5 h-5 text-yellow-400 fill-yellow-400 flex-shrink-0" />
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {contentArray.map((content) => {
          const isImage = content.type === 'image';
          const isLiked = (content.likes || []).some(l => l.userUuid === currentUserUuid);
          return (
            <div
              key={content.uuid}
              onClick={() => openContent(content)}
              className="relative aspect-[3/4] bg-gray-800 rounded-xl overflow-hidden cursor-pointer"
            >
              {getPreviewUrl(content) ? (
                <img src={getPreviewUrl(content)} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  {isImage ? (
                    <Image className="w-10 h-10 text-gray-600" />
                  ) : (
                    <Video className="w-10 h-10 text-gray-600" />
                  )}
                </div>
              )}
              {/* Bottom gradient overlay */}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2.5 pt-8">
                {content.question && (
                  <p className="text-white text-[11px] leading-tight line-clamp-2 mb-1">
                    {content.question.summary || content.question.question || ''}
                  </p>
                )}
                <span className="text-gray-400 text-[10px]">
                  {new Date(content.creation).toLocaleDateString()}
                </span>
                {/* Engagement icons */}
                <div className="flex items-center gap-3 mt-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleLike(content.uuid); }}
                    className="flex items-center gap-1 transition-colors"
                  >
                    <Heart className={`w-3.5 h-3.5 ${isLiked ? 'text-red-500 fill-red-500' : 'text-gray-300'}`} />
                    {(content.likeCount || 0) > 0 && (
                      <span className="text-gray-300 text-[10px]">{content.likeCount}</span>
                    )}
                  </button>
                  <div className="flex items-center gap-1 text-gray-300">
                    <MessageCircle className="w-3.5 h-3.5" />
                    {(content.commentCount || 0) > 0 && (
                      <span className="text-[10px]">{content.commentCount}</span>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(content.uuid); }}
                    className="flex items-center gap-1 transition-colors"
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${content.favorite ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                  </button>
                </div>
              </div>
              {/* Image type indicator */}
              {isImage && (
                <div className="absolute top-2 right-2">
                  <span className="bg-black/60 backdrop-blur-sm text-white text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Image size={10} />
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const filteredFavorites = filterBySearch(favorites);
  const filteredContents = filterBySearch(contents);

  const getFilteredContentsByCategory = () => {
    const categorized = {};
    filteredContents.forEach(content => {
      const category = normalizeCategory(content.question?.questionCategory);
      if (!categorized[category]) categorized[category] = [];
      categorized[category].push(content);
    });
    return categorized;
  };

  const categorizedContent = getFilteredContentsByCategory();
  const totalMemories = contents.length + favorites.length;
  const filteredTotal = filteredFavorites.length + filteredContents.length;
  const profileImageUrl = relative.profile?.profilePictureUrl || relative.profilePictureUrl || null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading memories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 sticky top-0 z-40 shadow-xl">
        <div className="p-4">
          <div className="flex items-center space-x-4 mb-3">
            <button onClick={onBack} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>
            {profileImageUrl ? (
              <img
                src={profileImageUrl}
                alt={relative.name}
                className="w-20 h-20 rounded-full object-cover shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-600 flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold">
                  {relative.name ? relative.name[0].toUpperCase() : '?'}
                </span>
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-xl font-bold">{relative.name}</h1>
              <p className="text-sm text-gray-400">
                {totalMemories} {totalMemories === 1 ? 'memory' : 'memories'}
              </p>
              {editingRelationship ? (
                <input
                  type="text"
                  value={relationshipValue}
                  onChange={(e) => setRelationshipValue(e.target.value)}
                  onBlur={async () => {
                    setEditingRelationship(false);
                    const trimmed = relationshipValue.trim();
                    if (trimmed && trimmed !== relative.relationship) {
                      try {
                        await ApiService.updateRelativeRelationship(relative.uuid, trimmed);
                        onRelativeUpdated?.({ ...relative, relationship: trimmed });
                        onShowToast?.('Relationship updated', 'success');
                      } catch (err) {
                        console.error('Failed to update relationship:', err);
                        setRelationshipValue(relative.relationship || '');
                        onShowToast?.('Failed to update relationship');
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur();
                    if (e.key === 'Escape') {
                      setRelationshipValue(relative.relationship || '');
                      setEditingRelationship(false);
                    }
                  }}
                  autoFocus
                  className="text-xs bg-gray-700 text-white px-2 py-1 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 w-32"
                />
              ) : (
                <p
                  className="text-xs text-gray-500 cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={() => setEditingRelationship(true)}
                  title="Click to edit relationship"
                >
                  {relative.relationship || 'Add relationship'}
                </p>
              )}
            </div>
          </div>

          {/* Search + View Mode Toggle */}
          <div className="flex items-center gap-2">
            {searchActive ? (
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search memories..."
                  autoFocus
                  className="w-full pl-9 pr-8 py-1.5 bg-gray-700 text-white text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500 placeholder-gray-400"
                />
                <button
                  onClick={() => { setSearchQuery(''); setSearchActive(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-600 rounded"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setSearchActive(true)}
                  className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors text-gray-400 hover:text-white"
                >
                  <Search className="w-4 h-4" />
                </button>
                <div className="flex-1" />
              </>
            )}
            <div className="bg-gray-700 rounded-lg p-1 flex">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Search result count */}
        {searchQuery.trim() && (
          <p className="text-sm text-gray-400 mb-4">
            {filteredTotal} {filteredTotal === 1 ? 'result' : 'results'} for "{searchQuery.trim()}"
          </p>
        )}

        {/* Bookmarked (Favorites) */}
        {filteredFavorites.length > 0 && (
          <div className="mb-6">
            <button
              onClick={() => toggleCategory('Bookmarked')}
              className="flex items-center space-x-2 mb-3 text-base font-semibold hover:text-gray-300 transition-colors"
            >
              <Bookmark className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span>Bookmarked</span>
              <span className="text-sm text-gray-400">({filteredFavorites.length})</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedCategories['Bookmarked'] ? 'rotate-180' : ''}`} />
            </button>
            {expandedCategories['Bookmarked'] && renderContentGrid(filteredFavorites)}
          </div>
        )}

        {/* Categories */}
        {getSortedCategories(categorizedContent).map(category => ({ category, categoryContents: categorizedContent[category] })).map(({ category, categoryContents }) => (
          <div key={category} className="mb-6">
            <button
              onClick={() => toggleCategory(category)}
              className="flex items-center space-x-2 mb-3 text-base font-semibold hover:text-gray-300 transition-colors"
            >
              <span>{category}</span>
              <span className="text-sm text-gray-400">({categoryContents.length})</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${expandedCategories[category] ? 'rotate-180' : ''}`} />
            </button>
            {expandedCategories[category] && renderContentGrid(categoryContents)}
          </div>
        ))}

        {/* Empty State */}
        {filteredContents.length === 0 && filteredFavorites.length === 0 && (
          <div className="text-center py-12">
            {searchQuery.trim() ? (
              <>
                <Search className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No results found</p>
                <p className="text-sm text-gray-500 mt-2">
                  Try a different search term
                </p>
              </>
            ) : (
              <>
                <Video className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">No memories yet</p>
                <p className="text-sm text-gray-500 mt-2">
                  Memories with {relative.name} will appear here
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Content Player Overlay */}
      {currentVideo && (
        <VideoPlayer
          content={currentVideo}
          currentUserUuid={currentUserUuid}
          onNext={playNext}
          onPrevious={playPrevious}
          onClose={() => setCurrentVideo(null)}
          onToggleFavorite={() => toggleFavorite(currentVideo.uuid)}
          onToggleLike={() => handleToggleLike(currentVideo.uuid)}
          onAddComment={(text) => handleAddComment(currentVideo.uuid, text)}
          onDeleteComment={(commentUuid) => handleDeleteComment(currentVideo.uuid, commentUuid)}
          onDeleteContent={() => handleDeleteContent(currentVideo.uuid)}
          isFavorite={favorites.some(f => f.uuid === currentVideo.uuid)}
          isLiked={(currentVideo.likes || []).some(l => l.userUuid === currentUserUuid)}
        />
      )}
    </div>
  );
}

export default RelativeProgressView;
